/** vendor */
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

/** lib */
import Loading from '../UI/Loading'
import { useToast } from '../../hooks/useToast'

/** state */
import { updateShoppinglist } from '../../services/user.service'
import { getUserMealplan } from '../../actions/user.actions'


import SectionHeading  from '../UI/SectionHeading'

const MealplanIngredientSidebar = ({ 
  shoppingListId,
  onClose = () => {},
  onAddIngredients = () => {},
  canEditList = false 
}) => {
  // vendor utils
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // a9 utils
  const toast = useToast()

  // ui states
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // user data
  const [selectedPlan, setSelectedPlan] = useState(undefined)
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(undefined)

  // form data
  const [checkedIngredients, setCheckedIngredients] = useState([])
  const [ingredientsInStores, setIngredientsInStores] = useState([])
  const [ingredientsNotInStores, setIngredientsNotInStores] = useState([])

  const ingredientOptions = useSelector(state => state.form_options?.ingredient_options || [])
  
  const mealplans = useSelector(state => state.user_data.mealplans)
  const foodstores = useSelector(state => state.user_data?.user_stores)
  const sharedWithMe = useSelector(state => state.user_data.shared_with_me)
  const mealplan = useSelector(state => state.current_mealplan)

  const sharedWithMeMealplans = useMemo(() => {
      return sharedWithMe.filter(m => m.type === 'UserMealplan')
  }, [sharedWithMe])

  const planOptions = useMemo(() => {
  if (mealplans && sharedWithMeMealplans)
      return [...mealplans, ...sharedWithMeMealplans];
  return mealplans || [];
  }, [mealplans, sharedWithMeMealplans]);

  const updateSelectedShoppingList = () => {
    setSaving(true)

    const ingredientsToAdd = [...checkedIngredients]
      .map(i => {
        const ingredientOption = ingredientOptions
          .find(ci => ci?.value === i.value)

        return {
          image: ingredientOption?.image, 
          name: ingredientOption?.name,
          quantity: parseInt(i?.quantity) < 1 ? 1 : Math.ceil(parseInt(i?.quantity)),
          value: i?.value,
          measurement: i?.measurement
        }
      })

      console.warn('update list', shoppingListId, ingredientsToAdd)

    if(shoppingListId) {
      updateShoppinglist(shoppingListId, {
        ingredients: ingredientsToAdd
      })
      .then((r) => {
        setSaving(false)
        onAddIngredients()
      }).catch(() => {
        setSaving(false)
      })
    }
  }

  useEffect(() => {
    if(!loading && mealplan && mealplan?.recipes?.length > 0 && foodstores && planOptions) {
      const meaplanIngredients = []
      const ingredientsInStoresIds = []
      const ingredientsNotinStoresIds = []

      mealplan?.recipes?.forEach((recipe, index) => {
        recipe?.recipe_ingredients.forEach((ingredient, i) => {
          meaplanIngredients.push(ingredient)
        })
      })

      meaplanIngredients.forEach(ingredient => {
        let inStock = false
        let itemToAdd = null

        for(const store of foodstores) {
          for(const item of store?.items) {
            if(item?.value === ingredient?.ingredient_id) { 
              inStock = true
              itemToAdd = item 
              break
            }
          } 

          if(inStock)
          {
            break
          }
        }
        
        if(inStock && itemToAdd) {
          ingredientsInStoresIds.push({
            value: ingredient?.ingredient_id, 
            quantity: ingredient?.quantity, 
            measurement: ingredient?.measurement
          })
        } else {
          ingredientsNotinStoresIds.push({
            value: ingredient?.ingredient_id, 
            quantity: ingredient?.quantity, 
            measurement: ingredient?.measurement
          })
        }
      })

      setCheckedIngredients(ingredientsNotinStoresIds)
      setIngredientsNotInStores(ingredientsNotinStoresIds)
      setIngredientsInStores(ingredientsInStoresIds)

      const currentOptionIndex = planOptions.findIndex(option => mealplan?.user_mealplan_id === option.value)

      setSelectedPlan(planOptions[currentOptionIndex])
      setSelectedPlanIndex(currentOptionIndex)        
    }
  }, [mealplan, loading, foodstores, planOptions])
    
  const handleOnToggleCheckedIngredient = (e, ingredient) => {
    if (e.target.checked) {
      // @todo fix 'units()' being the measurement it should be 'unit' this is caused somewhere else in the app
      const newIngredients = [...checkedIngredients, {
        value: ingredient?.ingredient_id,
        quantity: ingredient?.quantity || 1,
        measurement: ingredient?.measurement || 1
      }]

      setCheckedIngredients(newIngredients)
    } else {
      const newIngredients = checkedIngredients.filter((item) => item?.value !== ingredient?.ingredient_id)
      setCheckedIngredients(newIngredients)    
    }
  }

  const handlePlanSelect = async(e) => {
    setSelectedPlanIndex(parseInt(e.target.value))
    setSelectedPlan(planOptions[parseInt(e.target.value)])

    try {
      setLoading(true)
      
      await dispatch(getUserMealplan(planOptions[parseInt(e.target.value)].user_mealplan_id))
      
    } catch (error)  {
      setLoading(false)
    }
    finally {
      setLoading(false)
    }
  }

  const handleOnSendToList = () => {
    updateSelectedShoppingList()
  }

  return (                                         
    <div className="px-2 z-50 absolute p-2 top-0 right-0 bg-an_green-lighter1 h-full w-[300px] overflow-hidden">
        <div className="flex flex-col h-full">
          <select 
              value={selectedPlanIndex}
              disabled={saving} 
              id="sendToStore"
              onChange={(e) => { handlePlanSelect(e) } }
              className="text-sm rounded block w-full p-2 mb-2"
          >
              <option key={'none'}>select mealplan</option>
              {
                  planOptions.map((plan, index) => {
                      return <option key={index} value={index}>{plan?.name ?? 'plan: ' + index}</option>                                    
                  })
              }
          </select>

          {
              mealplan &&
                <div className="overflow-auto mx-auto w-full md:max-w-xl relative z-50 h-full bg-an_white-light rounded">
                  { 
                    loading && (<div className="h-[60vh] relative pt-40"><Loading /></div>) 
                  }
                  { 
                    !loading && Object.keys(mealplan).length !== 0 &&
                      <ul className="h-full">
                        {mealplan?.recipes?.map((recipe, index) => (
                            <li key={index}>
                              <div className="px-2">
                                <SectionHeading label={recipe?.name}/>
                              </div>

                              {
                                recipe?.recipe_ingredients?.map((ingredient, i) => {
                                    const ingredientDetail = ingredient.ingredient                                
                                    const instoreCount = ingredientsInStores.find(i => i?.value === ingredientDetail.ingredient_id)
                                    const isChecked = checkedIngredients?.find(i => i?.value === ingredientDetail.ingredient_id) ?? false

                                    return (
                                      <div className="py-2 px-4 text-sm flex justify-between items-center even:bg-an_white odd:bg-an_white-dark" key={i}>
                                        <div className={`${!instoreCount && 'text-an_orange-dark'} px-2 text-ellipsis`}>
                                          {ingredient?.quantity} {ingredient?.measurement} {ingredientDetail?.name}
                                        </div>
                                        
                                        {
                                          canEditList &&
                                            <div className="flex py-2 items-center pr-2">
                                              <input 
                                                onChange={(e) => {handleOnToggleCheckedIngredient(e, ingredient, recipe )}}
                                                checked={isChecked} 
                                                id={`sendTolist_${i}`} 
                                                type="checkbox" 
                                                value={ingredientDetail.ingredient_id}
                                                className="w-4 h-4 text-an_green-dark bg-an_white border-an_green-dark rounded" 
                                              />
                                            </div>
                                        }
                                      </div>
                                    )
                                })
                              }
                            </li>
                        ))}
                      </ul>
                  }
                </div>
          }
          
          {
            canEditList &&
            <div className="flex-shrink-0">
              <button
                disabled={!shoppingListId || saving}
                className={`${(!shoppingListId || saving) && 'opacity-60'} flex items-center justify-center relative mx-auto mt-3 mb-1 bg-an_orange w-40 text-border-orange min-w-32 text-an_white rounded font-medium border border-an_orange-darker3 text-sm px-5 py-2`}
                onClick={(e) => { handleOnSendToList(e) }}
              >
                <div className="whitespace-nowrap">add selected</div>
                {
                  saving && 
                    <div className="animate-pulse ml-2 absolute top-[6px] right-1">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 animate-spin ">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                    </div>
                } 
              </button>
            </div>
          }
        </div>
    </div>    
  )
}

export default MealplanIngredientSidebar